"use client";

import Link from "next/link";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { check_auth, loginStudent, requestLoginHelp } from "@/api";

const SUPPORT_NUMBER = "919220361603";
const SUPPORT_WHATSAPP_URL = `https://api.whatsapp.com/send/?phone=${SUPPORT_NUMBER}&text=Hi%20SOFAR%2C%20I%20need%20help%20with%20my%20account.`;

const initialHelpForm = {
  name: "",
  email: "",
  number: "",
  message: "",
};

function SigninContent() {
  const searchParams = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [helpForm, setHelpForm] = useState(initialHelpForm);
  const [helpStatus, setHelpStatus] = useState({ type: "", message: "" });
  const [isSubmittingHelp, setIsSubmittingHelp] = useState(false);
  const router = useRouter();

  useEffect(() => {
    const checkLoggedIn = async () => {
      const session = await check_auth();
      if (session) {
        router.replace("/dashboard");
        return;
      }
      setCheckingAuth(false);
    };
    checkLoggedIn();
  }, [router]);

  const isHelpValid = useMemo(
    () => helpForm.name && helpForm.email && helpForm.number,
    [helpForm]
  );

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      await loginStudent(email, password);
      const nextPath = searchParams.get("next");
      router.replace(nextPath || "/dashboard");
    } catch (err) {
      setError(err?.response?.data?.error || "Login failed. Try again.");
    }
  };

  const handleHelpSubmit = async (e) => {
    e.preventDefault();
    setHelpStatus({ type: "", message: "" });
    setIsSubmittingHelp(true);

    try {
      await requestLoginHelp(helpForm);
      setHelpStatus({
        type: "success",
        message: "Your request has been sent. Our team will contact you shortly.",
      });
      setHelpForm(initialHelpForm);
    } catch (err) {
      setHelpStatus({
        type: "error",
        message:
          err?.response?.data?.error ||
          "Could not send your help request right now.",
      });
    } finally {
      setIsSubmittingHelp(false);
    }
  };

  if (checkingAuth) return null;

  return (
    <section className="min-h-screen bg-[linear-gradient(160deg,#f4f8ff_0%,#ffffff_48%,#eef6ff_100%)] px-4 py-10">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="rounded-[28px] bg-[#0b4ea2] p-8 text-white shadow-[0_30px_80px_rgba(13,111,234,0.22)] lg:p-12">
          <img src="/sofar_logo.svg" alt="SOFAR" className="h-10 w-auto bg-white rounded px-3 py-2" />
          <h1 className="mt-10 max-w-xl text-4xl font-semibold leading-tight">
            Sign in to access your course dashboard and recorded sessions.
          </h1>
          <p className="mt-4 max-w-lg text-base leading-7 text-blue-50">
            Demo access is available through self-registration. If you need help
            recovering access or choosing the right course, use the support
            section on this page.
          </p>
          <div className="mt-10 rounded-2xl border border-white/20 bg-white/10 p-5">
            <p className="text-sm uppercase tracking-[0.24em] text-blue-100">
              Need Demo Access?
            </p>
            <p className="mt-2 text-xl font-medium">
              Create your account and start a demo course immediately.
            </p>
            <Link
              href="/register"
              className="mt-5 inline-flex rounded-full bg-white px-5 py-3 text-sm font-medium text-[#0b4ea2]"
            >
              Create Demo Account
            </Link>
          </div>
        </div>

        <div className="rounded-[28px] border border-[#d7e7ff] bg-white p-6 shadow-[0_20px_60px_rgba(10,45,90,0.08)] lg:p-8">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-[#0d6fea]">
                Student Login
              </p>
              <h2 className="mt-2 text-2xl font-semibold text-[#1a1a1a]">
                Welcome back
              </h2>
            </div>
            <Link href="/" className="text-sm text-[#0d6fea]">
              Back to home
            </Link>
          </div>

          {error ? (
            <p className="mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">
              {error}
            </p>
          ) : null}

          <form onSubmit={handleLogin} className="mt-6 flex flex-col gap-4">
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <input
              type="password"
              placeholder="Enter your password"
              className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />

            <button
              type="submit"
              className="rounded-2xl bg-[#0d6fea] px-4 py-3 font-medium text-white transition hover:opacity-90"
            >
              Login
            </button>
          </form>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm">
            <Link href="/register" className="text-[#0d6fea]">
              Create a demo account
            </Link>
            <button
              type="button"
              onClick={() => setShowHelp((prev) => !prev)}
              className="text-[#1a1a1a]"
            >
              Need more help?
            </button>
          </div>

          {showHelp ? (
            <div className="mt-6 rounded-3xl bg-[#f6f9ff] p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-lg font-semibold text-[#1a1a1a]">
                    Account Help
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Send your details and we will notify the team on WhatsApp.
                  </p>
                </div>
                <a
                  href={SUPPORT_WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full border border-[#0d6fea] px-4 py-2 text-xs font-medium text-[#0d6fea]"
                >
                  Chat on WhatsApp
                </a>
              </div>

              <form onSubmit={handleHelpSubmit} className="mt-4 flex flex-col gap-3">
                <input
                  type="text"
                  placeholder="Your name"
                  className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
                  value={helpForm.name}
                  onChange={(e) =>
                    setHelpForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                />
                <input
                  type="email"
                  placeholder="Your email"
                  className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
                  value={helpForm.email}
                  onChange={(e) =>
                    setHelpForm((prev) => ({ ...prev, email: e.target.value }))
                  }
                  required
                />
                <input
                  type="tel"
                  placeholder="Your phone number"
                  className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
                  value={helpForm.number}
                  onChange={(e) =>
                    setHelpForm((prev) => ({ ...prev, number: e.target.value }))
                  }
                  required
                />
                <textarea
                  rows={3}
                  placeholder="Tell us what you need help with"
                  className="w-full rounded-2xl border border-[#d5dded] px-4 py-3 outline-none transition focus:border-[#0d6fea]"
                  value={helpForm.message}
                  onChange={(e) =>
                    setHelpForm((prev) => ({ ...prev, message: e.target.value }))
                  }
                />
                {helpStatus.message ? (
                  <p
                    className={`text-sm ${
                      helpStatus.type === "success" ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {helpStatus.message}
                  </p>
                ) : null}
                <button
                  type="submit"
                  disabled={!isHelpValid || isSubmittingHelp}
                  className="rounded-2xl bg-[#1a1a1a] px-4 py-3 font-medium text-white disabled:opacity-60"
                >
                  {isSubmittingHelp ? "Sending..." : "Request Help"}
                </button>
              </form>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

export default function Signin() {
  return (
    <Suspense fallback={null}>
      <SigninContent />
    </Suspense>
  );
}
